/**
 * CycleReportView — Brief G §7
 *
 * Renders the Cycle Report surface from the cycle_reports table.
 * Sections:
 *  1. Header: cycle name, version badge, legitimacy state, ratifier, date
 *  2. The Priorities: ranked list from plan_of_record.ranked_items
 *  3. Decision Trail: loop/session counts, proposal link, overrides
 *  4. Rationale & Assumptions: rationale, decision note, assumptions, risks
 *  5. Version History: collapsed list of prior versions
 *  6. Publish Action: available when legitimacy_state = 'ratified' + authorized
 *  7. Planning Handoff: "Open in Planning" when published
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { StructureAtTheTime } from '../hierarchy/StructureAtTheTime';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Globe,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  ExternalLink,
  FileText,
  TrendingUp,
  ArrowUpRight,
  MessageSquare,
  AlertTriangle,
  Loader2,
  Scale,
  Shield,
  GitCommit,
  History,
} from 'lucide-react';
import { useCycleReportStore } from '../../stores/prioritization/cycleReportStore';
import type { CycleReport, CycleReportHistoryRow, LegitimacyState } from '../../stores/prioritization/cycleReportStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useCycleStore } from '../../stores/prioritization/degapProcessStore';
import { cycleReportPublishAllowed, cycleReportPublishLine } from '../../lib/cycleReportPublishRight';
import { DecisionTrailPanel } from './DecisionTrailPanel';
import { useDecisionTrailParam } from '../../hooks/prioritization/useDecisionTrailParam';
import { SignalTraceabilitySection } from '../signals/SignalTraceabilitySection';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { RankMark } from '@/components/prioritization/RankMark';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { CallSentence } from './decisions/CallSentence';
import { CYCLE_REPORT_DECISION_GAP_TEXT, decisionForCycleReport } from '../../lib/call/rankingDecision';
import { fetchListApprovalDecisionIds } from '../../lib/call/rankingDecisionService';
import { loadInversionAcknowledgments } from '../../lib/inversionAcknowledgmentService';
import { acknowledgedInversionsByHigherItem, type InversionAcknowledgment } from '../../lib/prioritizationInversionDetector';
import { InversionMark } from './priorities/InversionMark';
import { usePriorityListContests } from '../../hooks/usePriorityListContests';
import { didNotAgreeWords } from '../../lib/contestService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

/** Real Planning surface — /prioritization/planning does not exist and falls through to home. */
function planningHandoffHref(report: CycleReport): string {
  const params = new URLSearchParams({ catalog: '1', create: '1' });
  if (report.prioritySetId) {
    params.set('inputType', 'published_priority_list');
    params.set('inputId', report.prioritySetId);
  }
  if (report.cycleName) {
    params.set('suggestedName', `${report.cycleName} — Plan`);
  }
  return `/planning/plans?${params.toString()}`;
}

function LegitimacyBadge({ state }: { state: LegitimacyState }) {
  if (state === 'published') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-semibold">
        <Globe className="h-3.5 w-3.5" />
        Published
      </span>
    );
  }
  if (state === 'ratified') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-semibold">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Ratified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-semibold">
      <RotateCcw className="h-3.5 w-3.5" />
      Superseded
    </span>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface CycleReportViewProps {
  cycleId: string;
  /** Called when the report is loaded (for parent state sync). */
  onReportLoaded?: (report: CycleReport | null) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CycleReportView({ cycleId, onReportLoaded }: CycleReportViewProps) {
  const userId = useUserStore(s => s.userId);
  const can = useUserStore(s => s.can);
  // Who may publish is the view's own question, asked the way publish_cycle_report
  // asks it (migration 1008) — never a caller's say-so (build contract §2.8).
  const cycleOwnerId = useCycleStore(s => s.getCycle(cycleId)?.ownerId ?? null);
  const canPublish = cycleReportPublishAllowed({ userId, cycleOwnerId, can });

  const fetchCurrentCycleReport = useCycleReportStore(s => s.fetchCurrentCycleReport);
  const fetchCycleReportHistory = useCycleReportStore(s => s.fetchCycleReportHistory);
  const publishCycleReport = useCycleReportStore(s => s.publishCycleReport);

  const [report, setReport] = useState<CycleReport | null>(null);
  const actorNames = useActorDisplayMap([report?.ratifiedBy, cycleOwnerId]);
  const ratifiedByName = report?.ratifiedBy ? actorNames[report.ratifiedBy] ?? null : null;
  const cycleOwnerName = cycleOwnerId ? actorNames[cycleOwnerId] ?? null : null;
  const [history, setHistory] = useState<CycleReportHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const [publishUrl, setPublishUrl] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // "Why is this #N?" — the Decision Trail inspector, URL-addressable via ?why=
  const { trailItemId, openTrail, closeTrail } = useDecisionTrailParam();

  // The call behind this report and who did not agree, drawn with its standing;
  // acknowledged inversions drawn on the rows they qualify (owner, 2026-10-30:
  // a standing is never drawn alone). The report's list carries the contests
  // and the acknowledgments; the acceptance that wrote the report, or the
  // list's own decision, carries the call.
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const [decisionsReady, setDecisionsReady] = useState(decisions.length > 0);
  const [listDecisionIds, setListDecisionIds] = useState<ReadonlyMap<string, string> | null>(null);
  // Bumped after an act on this page writes to the record, so the list's
  // decision ids and acknowledgments are read again.
  const [listFactsVersion, setListFactsVersion] = useState(0);
  const [acknowledgments, setAcknowledgments] = useState<InversionAcknowledgment[] | null>(null);
  const reportListId = report?.prioritySetId ?? null;
  const contests = usePriorityListContests(reportListId, null, userId);

  useEffect(() => {
    if (decisions.length > 0) {
      setDecisionsReady(true);
      return;
    }
    let cancelled = false;
    void fetchDecisions().finally(() => {
      if (!cancelled) setDecisionsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [decisions.length, fetchDecisions]);

  useEffect(() => {
    if (!reportListId) {
      setListDecisionIds(new Map());
      setAcknowledgments([]);
      return;
    }
    let cancelled = false;
    setListDecisionIds(null);
    setAcknowledgments(null);
    void Promise.all([
      fetchListApprovalDecisionIds([reportListId]).catch(() => new Map<string, string>()),
      loadInversionAcknowledgments(reportListId).catch(() => [] as InversionAcknowledgment[]),
    ]).then(([ids, acks]) => {
      if (cancelled) return;
      setListDecisionIds(ids);
      setAcknowledgments(acks);
    });
    return () => {
      cancelled = true;
    };
  }, [reportListId, listFactsVersion]);

  const reportCall = useMemo(
    () => (report && decisionsReady && listDecisionIds ? decisionForCycleReport(report, decisions, listDecisionIds) : null),
    [report, decisionsReady, listDecisionIds, decisions],
  );
  const acknowledgedByItem = useMemo(() => acknowledgedInversionsByHigherItem(acknowledgments ?? []), [acknowledgments]);
  const acknowledgerIds = useMemo(() => (acknowledgments ?? []).map((a) => a.acknowledgedBy), [acknowledgments]);
  const acknowledgerNames = useActorDisplayMap(acknowledgerIds);

  // Keep a stable ref to the callback so it never appears in useCallback deps
  // (prevents the inline arrow passed by the parent from triggering re-fetches).
  const onReportLoadedRef = useRef(onReportLoaded);
  useEffect(() => { onReportLoadedRef.current = onReportLoaded; });

  const loadReport = useCallback(async () => {
    setLoading(true);
    const [r, h] = await Promise.all([
      fetchCurrentCycleReport(cycleId),
      fetchCycleReportHistory(cycleId),
    ]);
    setReport(r);
    setHistory(h);
    setLoading(false);
    onReportLoadedRef.current?.(r);
  }, [cycleId, fetchCurrentCycleReport, fetchCycleReportHistory]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  /** Named guard messages per correction §5 */
  function getPublishBlockReason(): string | null {
    if (!report) return 'No report available';
    if (report.legitimacyState !== 'ratified') return 'Priorities must be ratified before publishing';
    if (!canPublish) return cycleReportPublishLine(cycleOwnerName);
    if (!publishMessage.trim()) return 'A publication message is required';
    return null;
  }

  const handlePublish = async () => {
    if (!report || !userId) return;
    const blockReason = getPublishBlockReason();
    if (blockReason) {
      setPublishError(blockReason);
      return;
    }
    setPublishing(true);
    setPublishError(null);
    const result = await publishCycleReport({
      reportId: report.id,
      publicationMessage: publishMessage.trim(),
      publicationUrl: publishUrl.trim() || undefined,
      actorId: userId,
      workspaceId: report.workspaceId ?? '',
    });
    setPublishing(false);
    if (result.success) {
      setPublishOpen(false);
      setPublishMessage('');
      setPublishUrl('');
      void loadReport();
      // Publishing wrote a Decision for the report and one for its list
      // (migration 1008): read the record again, so the call behind this
      // report is the publication, not the acceptance it fell back to.
      void fetchDecisions();
      setListFactsVersion((v) => v + 1);
    } else {
      setPublishError(result.error ?? 'Failed to publish. Please try again.');
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading cycle report…</span>
      </div>
    );
  }

  // ── Empty state ──
  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <FileText className="h-10 w-10 text-[var(--text-ter)]" />
        <div>
          <p className="font-medium text-[var(--text-sec)]">Priorities not yet ratified</p>
          <p className="text-sm text-[var(--text-ter)] mt-1 max-w-sm">
            Complete a Catchball loop and have the executive accept a proposal to produce the cycle report.
          </p>
        </div>
      </div>
    );
  }

  const por = report.planOfRecord;
  const aboveCut = por?.ranked_items?.filter(i => i.above_cut_line) ?? [];
  const belowCut = por?.ranked_items?.filter(i => !i.above_cut_line) ?? [];
  const isRatified = report.legitimacyState === 'ratified';
  const isPublished = report.legitimacyState === 'published';

  return (
    <div className="space-y-6">

      {/* ── 1. Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-[var(--rule)]">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {report.cycleName ?? 'Cycle Report'}
            </h2>
            <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]">
              v{report.version}
            </span>
            <LegitimacyBadge state={report.legitimacyState} />
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            Ratified {fmtDate(report.ratifiedAt)}
            {ratifiedByName && <> · by <span className="font-medium text-[var(--text-sec)]">{ratifiedByName}</span></>}
            {isPublished && (
              <> · Published {fmtDate(report.publishedAt)}</>
            )}
            {reportListId && (
              <>
                {' · '}
                <span data-testid="report-did-not-agree" className={contests.openContests.length > 0 ? 'text-[var(--coral)]' : ''}>
                  {didNotAgreeWords(contests.openContests.length)}
                </span>
              </>
            )}
          </p>
        </div>

        {/* Publish — drawn only for someone publish_cycle_report will accept; everyone
            else gets the plain line, never a disabled button (build contract §2.8) */}
        {isRatified && canPublish && (
          <button
            type="button"
            onClick={() => setPublishOpen(v => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors bg-primary-600 hover:bg-primary-700 text-white"
          >
            <Globe className="h-4 w-4" />
            Publish Priorities
          </button>
        )}
        {isRatified && !canPublish && (
          <p className="max-w-xs text-right text-xs text-[var(--text-ter)]">{cycleReportPublishLine(cycleOwnerName)}</p>
        )}

        {/* Planning handoff — when published */}
        {isPublished && (
          <Link
            to={planningHandoffHref(report)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm font-medium transition-colors"
          >
            <ArrowUpRight className="h-4 w-4" />
            Open in Planning
          </Link>
        )}
      </div>

      {/* ── Report missing warning (if ratification succeeded but report wasn't generated) ── */}
      {/* This banner only shows when navigating here post-accept but report is null — handled by empty state above */}

      {/* ── 6. Publish Form (inline, collapsible) ── */}
      {isRatified && canPublish && publishOpen && (
        <div className="border border-primary-200 bg-primary-50/60 dark:bg-primary-900/20 p-5 space-y-4">
          <h3 className="font-semibold text-primary-900 dark:text-primary-200">Publish priorities</h3>
          <p className="text-sm text-primary-700 dark:text-primary-300">
            Publishing makes these priorities visible to stakeholders and enables the Planning handoff.
            This action transitions the report from <strong>Ratified</strong> to <strong>Published</strong>.
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                Publication message <span className="text-[var(--coral)]">*</span>
              </label>
              <textarea
                rows={3}
                value={publishMessage}
                onChange={e => setPublishMessage(e.target.value)}
                placeholder="Summarize the decision and what stakeholders should know…"
                className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                External URL (optional)
              </label>
              <input
                type="url"
                value={publishUrl}
                onChange={e => setPublishUrl(e.target.value)}
                placeholder="https://docs.company.com/priorities/q2-2026"
                className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>

            {publishError && (
              <p className="text-sm text-[var(--coral)] flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {publishError}
              </p>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setPublishOpen(false); setPublishError(null); }}
                className="px-4 py-2 border border-[var(--rule)] text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={publishing || !publishMessage.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 transition-colors"
              >
                {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
                Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── The call behind this report: the standing is never drawn alone (owner, 2026-10-30) ── */}
      <section data-testid="report-call" className="border border-[var(--rule)] dark:border-[var(--rule)] p-4">
        {!reportCall ? (
          <p className="text-sm text-[var(--text-ter)]">Reading the decision record…</p>
        ) : reportCall.decision ? (
          <>
            <CallSentence compact eyebrow="The call behind this report" decision={reportCall.decision} decisions={decisions} />
            <p className="mt-2 text-xs text-[var(--text-ter)]">
              {reportCall.basis === 'report'
                ? 'Read from the decision publishing this report wrote: the same cycle, the same instant.'
                : reportCall.basis === 'list'
                  ? "Read from the decision the priority list's own ratification or publication wrote."
                  : 'Read from the acceptance that produced this report: the same cycle, the same instant.'}
            </p>
          </>
        ) : (
          <>
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">The call behind this report</p>
            <p className="mt-1 text-sm text-[var(--text-sec)]">{CYCLE_REPORT_DECISION_GAP_TEXT}</p>
          </>
        )}
      </section>

      {/* Publication metadata — when published */}
      {isPublished && report.publicationMessage && (
        <div className="border border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20 p-4 space-y-2">
          <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)]">
            <Globe className="h-4 w-4" />
            <span className="text-sm font-semibold">Publication note</span>
          </div>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{report.publicationMessage}</p>
          {report.publicationUrl && (
            <a
              href={report.publicationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {report.publicationUrl}
            </a>
          )}
        </div>
      )}

      {/* ── 2. The Priorities ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-primary-600" />
          <h3 className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">The Priorities</h3>
          {por?.cut_line_position && (
            <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              · Cut line at #{por.cut_line_position} · {aboveCut.length} above / {belowCut.length} below
            </span>
          )}
        </div>

        {!por?.ranked_items?.length ? (
          <p className="text-sm text-[var(--text-ter)] italic">No ranked items in this report.</p>
        ) : (
          <div className="space-y-1">
            {por.ranked_items.map((item, index) => {
              // cut_line_position is how many items sit above the line, so the line follows that item.
              const isCutLine = item.rank === por.cut_line_position && index < por.ranked_items.length - 1;
              const attrs = item.key_attributes ?? {};
              return (
                <div key={item.item_id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openTrail(item.item_id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTrail(item.item_id); } }}
                    title={`Why is this #${item.rank}? Open the decision trail`}
                    className={`group flex items-center gap-3 px-4 py-3  border transition-colors cursor-pointer ${
                    item.above_cut_line
                      ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border-[var(--rule)] dark:border-[var(--rule)] hover:border-primary-300 dark:hover:border-[var(--rule-bright)]'
                      : 'bg-[var(--surface)] dark:bg-[var(--surface)] border-[var(--rule)] dark:border-[var(--rule)] opacity-75 hover:opacity-100'
                  }`}>
                    <RankMark rank={item.rank} official={false} aboveTheLine={item.above_cut_line} />
                    <span className={`flex-1 text-sm font-medium ${
                      item.above_cut_line ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'
                    }`}>
                      {item.title}
                    </span>
                    <ItemRelationshipFlags itemId={item.item_id} />
                    {item.override_applied && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)] font-medium">
                        Override
                      </span>
                    )}
                    {Object.keys(attrs).length > 0 && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {Object.entries(attrs).slice(0, 3).map(([k, v]) => (
                          <span key={k} className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                            <span className="text-[var(--text-ter)]">{k}:</span> {String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      Why #{item.rank}?
                    </span>
                  </div>
                  {(acknowledgedByItem.get(item.item_id) ?? []).map((a) => (
                    <div key={a.id} className="px-4">
                      <InversionMark
                        acknowledgment={a}
                        lowerTitle={por.ranked_items.find((r) => r.item_id === a.lowerRankedItemId)?.title ?? null}
                        acknowledgedByName={acknowledgerNames[a.acknowledgedBy] ?? null}
                      />
                    </div>
                  ))}
                  {isCutLine && (
                    <div className="flex items-center gap-2 my-2">
                      <div className="flex-1 border-t-2 border-dashed border-[var(--mustard)]" />
                      <span className="text-xs font-semibold text-[var(--mustard)] whitespace-nowrap">— Cut line —</span>
                      <div className="flex-1 border-t-2 border-dashed border-[var(--mustard)]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── 3. Scoring Basis ── */}
      <section className="border border-[var(--rule)] dark:border-[var(--rule)] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Scoring Basis</h3>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Criteria and weights that produced the accepted ranking
          </span>
        </div>

        {report.scoringBasis?.criteria && report.scoringBasis.criteria.length > 0 ? (
          <div className="overflow-hidden border border-[var(--rule)] dark:border-[var(--rule)]">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--surface)] dark:bg-[var(--surface-raised)]">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide">Criterion</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide">Weight</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {report.scoringBasis.criteria.map((c) => (
                  <tr key={c.key} className="bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
                    <td className="px-4 py-2 text-[var(--text-pri)] dark:text-[var(--text-pri)]">{c.label}</td>
                    <td className="px-4 py-2 text-right font-mono text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {c.weight != null ? `${(c.weight * 100).toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[var(--text-ter)] italic">
            No criteria recorded. Criteria are captured when they exist in the workspace at ratification time.
          </p>
        )}

        <div className="flex flex-wrap gap-4 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {report.scoringBasis?.value_snapshot_id && (
            <span>Value snapshot: <span className="font-mono">{report.scoringBasis.value_snapshot_id.slice(0, 8)}…</span></span>
          )}
          {por?.value_collection_snapshot_id && !report.scoringBasis?.value_snapshot_id && (
            <span>Value snapshot: <span className="font-mono">{por.value_collection_snapshot_id.slice(0, 8)}…</span></span>
          )}
          {report.scoringBasis?.scoring_method && (
            <span>Method: {report.scoringBasis.scoring_method.replace(/_/g, ' ')}</span>
          )}
          {por?.session_count != null && (
            <span>{por.session_count} scoring session{por.session_count !== 1 ? 's' : ''}</span>
          )}
        </div>
      </section>

      {/* ── 4. Ratification Record ── */}
      <section className="border border-[var(--rule)] dark:border-[var(--rule)] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-[var(--text-ter)]" />
          <h3 className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Ratification Record</h3>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Items considered', value: por?.total_items_considered ?? '—' },
            { label: 'Loop turns', value: por?.loop_count ?? '—' },
            { label: 'Sessions', value: por?.session_count ?? '—' },
            { label: 'Version', value: report.version },
          ].map(({ label, value }) => (
            <div key={label} className="bg-[var(--surface)] dark:bg-[var(--surface-raised)] p-3 text-center">
              <div className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{value}</div>
              <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{label}</div>
            </div>
          ))}
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-0.5">Approved by</dt>
            <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">{ratifiedByName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-0.5">Approved on</dt>
            <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">{fmtDate(report.ratifiedAt)}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-0.5">Under authority</dt>
            <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {report.ratificationAuthority ?? <span className="text-[var(--text-ter)] italic">Not recorded</span>}
            </dd>
          </div>
          {report.proposalId && (
            <div>
              <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-0.5">Source proposal</dt>
              <dd>
                <Link
                  to={`/prioritization/cascade/proposal/${report.proposalId}`}
                  className="text-primary-600 hover:text-primary-800 font-medium inline-flex items-center gap-1"
                >
                  View proposal
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </dd>
            </div>
          )}
        </dl>

        {report.rationale && (
          <div>
            <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-1">Rationale</dt>
            <dd className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] leading-relaxed">{report.rationale}</dd>
          </div>
        )}
      </section>

      {/* ── 5. Amendments — deviations from scored ranking ── */}
      {((report.amendmentLog && report.amendmentLog.length > 0) ||
        (report.overrides && Array.isArray(report.overrides) && (report.overrides as unknown[]).length > 0)) && (
        <section className="border border-[var(--mustard)] bg-[var(--mustard-dim)]/40 dark:bg-[var(--mustard)]/10 dark:border-[var(--mustard)] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <GitCommit className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)]" />
            <h3 className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Amendments</h3>
            <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Deviations from scored ranking
            </span>
          </div>

          {/* amendment_log entries (migration 478+) */}
          {report.amendmentLog && report.amendmentLog.length > 0 && (
            <ul className="space-y-2">
              {report.amendmentLog.map((entry, i) => (
                <li key={i} className="border border-[var(--mustard)] dark:border-[var(--mustard)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                      entry.type === 'rank_change'
                        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]'
                        : entry.type === 'added'
                        ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]'
                        : 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]'
                    }`}>
                      {entry.type === 'rank_change' ? 'Rank override'
                        : entry.type === 'added' ? 'Added'
                        : 'Removed'}
                    </span>
                    <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{entry.item_title}</span>
                    {entry.type === 'rank_change' && entry.previous_rank != null && entry.new_rank != null && (
                      <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                        #{entry.previous_rank} → #{entry.new_rank}
                      </span>
                    )}
                  </div>
                  {entry.justification ? (
                    <p className="mt-1 text-[var(--text-sec)] dark:text-[var(--text-sec)]">{entry.justification}</p>
                  ) : (
                    <p className="mt-1 text-[var(--text-ter)] dark:text-[var(--text-dim)] italic text-xs">Justification not recorded</p>
                  )}
                  {entry.authorized_at && (
                    <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      Authorized {fmtDate(entry.authorized_at)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Legacy overrides (pre-migration 478) */}
          {report.overrides && Array.isArray(report.overrides) && (report.overrides as unknown[]).length > 0 &&
            (!report.amendmentLog || report.amendmentLog.length === 0) && (
            <ul className="space-y-1">
              {(report.overrides as Array<Record<string, unknown>>).map((o, i) => (
                <li key={i} className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
                  <AlertTriangle className="inline h-3.5 w-3.5 mr-1 opacity-60" />
                  {String(o.item_title ?? o.item_id ?? `Item ${i + 1}`)}: {String(o.reason ?? o.note ?? 'Override applied')}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── Decision notes (decision_note, assumptions, risks) ── */}
      <StructureAtTheTime reportId={report.id} />

      {(report.decisionNote || report.assumptions || report.risks) && (
        <section className="border border-[var(--rule)] dark:border-[var(--rule)] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[var(--text-ter)]" />
            <h3 className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Decision Notes</h3>
          </div>
          {report.decisionNote && (
            <div>
              <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-1">Decision note</dt>
              <dd className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] leading-relaxed">{report.decisionNote}</dd>
            </div>
          )}
          {report.decisionConfidence && (
            <div>
              <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-1">Decision confidence</dt>
              <dd className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)]">{report.decisionConfidence}</dd>
            </div>
          )}
          {report.assumptions && (
            <div>
              <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-1">Assumptions</dt>
              <dd className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] leading-relaxed">{report.assumptions}</dd>
            </div>
          )}
          {report.risks && (
            <div>
              <dt className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide mb-1">Risks</dt>
              <dd className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] leading-relaxed">{report.risks}</dd>
            </div>
          )}
        </section>
      )}

      {/* ── 6. Change Log ── */}
      {history.length === 1 && (
        <p className="text-sm text-[var(--text-ter)] italic">
          This is the first version of this cycle report.
        </p>
      )}
      {history.length > 1 && (
        <section>
          <button
            type="button"
            onClick={() => setHistoryOpen(v => !v)}
            className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] transition-colors"
          >
            {historyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <History className="h-4 w-4" />
            Change log ({history.length} versions)
          </button>

          {historyOpen && (
            <div className="mt-3 border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-[var(--surface)] dark:bg-[var(--surface-raised)]">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide">Version</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide">State</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide">Ratified</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide">Published</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide hidden sm:table-cell">What changed / rationale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {history.map(row => {
                    const changes = row.amendmentLog && row.amendmentLog.length > 0;
                    const changeNote = (
                      row.amendmentLog && row.amendmentLog.length > 0 ? (
                          <span className="text-xs">
                            {row.amendmentLog.map((a, i) => (
                              <span key={i} className="block">
                                {a.type === 'rank_change'
                                  ? `${a.item_title}: #${a.previous_rank}→#${a.new_rank}`
                                  : a.type === 'added'
                                  ? `+ ${a.item_title}`
                                  : `− ${a.item_title}`}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="truncate">{row.rationale ?? '—'}</span>
                        )
                    );
                    const rowClass = row.id === report.id ? 'bg-primary-50/50 dark:bg-primary-900/10' : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)]';
                    return (
                      <Fragment key={row.id}>
                        <tr className={rowClass}>
                          <td className="px-4 py-2.5 font-mono text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                            v{row.version}
                            {row.id === report.id && (
                              <span className="ml-1 text-xs text-primary-600 font-sans">(current)</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <LegitimacyBadge state={row.legitimacyState} />
                          </td>
                          <td className="px-4 py-2.5 text-[var(--text-ter)] dark:text-[var(--text-sec)]">{fmtDate(row.ratifiedAt)}</td>
                          <td className="px-4 py-2.5 text-[var(--text-ter)] dark:text-[var(--text-sec)]">{fmtDate(row.publishedAt)}</td>
                          <td className="px-4 py-2.5 text-[var(--text-ter)] dark:text-[var(--text-sec)] hidden sm:table-cell max-w-xs">
                            {changeNote}
                          </td>
                        </tr>
                        {/* Below sm the column is not drawn; the change or rationale reads under the row (audit #43). */}
                        {(changes || row.rationale) && (
                          <tr className={`sm:hidden !border-t-0 ${rowClass}`}>
                            <td colSpan={4} className="px-4 pb-2.5 pt-0 text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                              {changeNote}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── 7. Planning Handoff ── */}
      {isPublished && (
        <section className="border border-[var(--green)] bg-[var(--green-dim)]/50 dark:bg-[var(--green)]/10 dark:border-[var(--green)] p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Planning Handoff</h3>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
                These priorities are now published. Open the Planning surface to begin execution planning seeded from this cycle report.
              </p>
            </div>
            <Link
              to={planningHandoffHref(report)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--green)] hover:bg-[var(--green)] text-white text-sm font-semibold transition-colors whitespace-nowrap flex-shrink-0"
            >
              <ArrowUpRight className="h-4 w-4" />
              Open in Planning
            </Link>
          </div>
        </section>
      )}

      {/* ── 8. Signal traceability (Signal Framework v2, SIG-5; module-gated) ── */}
      <SignalTraceabilitySection cycleId={cycleId} />

      {/* ── "Why is this #N?" — Decision Trail inspector ── */}
      <DecisionTrailPanel
        open={trailItemId != null}
        onClose={closeTrail}
        itemId={trailItemId}
        rankLabel={por?.ranked_items?.find(i => i.item_id === trailItemId)?.rank ?? null}
        contextLabel={`Published Priority List v${report.version}`}
      />
    </div>
  );
}

export default CycleReportView;
